import React, { useState, useEffect } from "react";
import SelectionSummary from "../../pages/Enroll/SelectionSummary";
import RadioGroup from "./inputs/RadioGroup";
import InputField from "./inputs/InputField";
import ProgressBar from "./ProgressBar";
import dealerData from "../../data/dealers.json";
import "./JotFormEmbed.scss";
import { useSearchParams } from "react-router-dom";
import CollapseButton from "./inputs/CollapseButton";
import TermsAgreement from "../../assets/Master_Subscription_And_Services_Agreement.pdf";

const FORM_ID = "260067105259151";

const JotFormComponent = () => {
	const [searchParams] = useSearchParams();
	const [currentPage, setCurrentPage] = useState(1);
	const [selections, setSelections] = useState({
		agency: "",
		package: "",
	});

	const [dealerInfo, setDealerInfo] = useState({
		dealerCode: "",
		dealershipName: "",
		dealershipContact: { first: "", last: "" },
		emailAddress: "",
		phoneNumber: { full: "" },
		dealershipContact22: { first: "", last: "" },
		emailAddress23: "",
		phoneNumber24: { full: "" },

		// Additional contacts matching JotForm field IDs
		dealershipContact56: { first: "", last: "" },
		emailAddress57: "",
		phoneNumber58: { full: "" },

		dealershipContact61: { first: "", last: "" },
		emailAddress62: "",
		phoneNumber63: { full: "" },

		dealershipContact66: { first: "", last: "" },
		emailAddress67: "",
		phoneNumber68: { full: "" },

		dealershipContact71: { first: "", last: "" },
		emailAddress72: "",
		phoneNumber73: { full: "" },
	});

	// eslint-disable-next-line no-unused-vars
	const [isSubmitting, setIsSubmitting] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [submitStatus, setSubmitStatus] = useState(null);

	// Add validation state
	const [validation, setValidation] = useState({
		emailAddress: true,
		emailAddress23: true,
		emailAddress72: true,
		phoneNumber: true,
		phoneNumber24: true,
		phoneNumber73: true,
	});

	const [packageError, setPackageError] = useState(false);
	const [phoneError, setPhoneError] = useState(false);
	const [emailError, setEmailError] = useState(false);

	// Update additional contacts state to handle up to 5 contacts
	const [additionalContacts, setAdditionalContacts] = useState([]);
	const [openContactSections, setOpenContactSections] = useState([]);

	// Add validation functions
	const validateEmail = (email) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(email);
	};

	const validatePhone = (phone) => {
		const re = /^\(\d{3}\)\s\d{3}-\d{4}$/;
		return re.test(phone);
	};

	const formatPhoneNumber = (value) => {
		// Remove all non-digits
		const numbers = value.replace(/\D/g, "");

		// Format the number as (XXX) XXX-XXXX
		if (numbers.length >= 10) {
			return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(
				6,
				10,
			)}`;
		}
		return value;
	};

	// Update the phone number handler
	const handlePhoneChange = (e, field, contactIndex) => {
		const formattedNumber = formatPhoneNumber(e.target.value);
		const isValid = validatePhone(formattedNumber);

		if (typeof contactIndex === "number") {
			// Handle additional contacts
			const newContacts = [...additionalContacts];
			newContacts[contactIndex].phoneNumber.full = formattedNumber;
			newContacts[contactIndex].phoneValid = isValid;
			setAdditionalContacts(newContacts);
		} else {
			// Handle primary contacts as before
			if (field === "primary") {
				setDealerInfo((prev) => ({
					...prev,
					phoneNumber: { full: formattedNumber },
				}));
				setValidation((prev) => ({ ...prev, phoneNumber: isValid }));
			} else if (field === "secondary") {
				setDealerInfo((prev) => ({
					...prev,
					phoneNumber24: { full: formattedNumber },
				}));
				setValidation((prev) => ({ ...prev, phoneNumber24: isValid }));
			} else if (field === "tertiary") {
				setDealerInfo((prev) => ({
					...prev,
					phoneNumber73: { full: formattedNumber },
				}));
				setValidation((prev) => ({ ...prev, phoneNumber73: isValid }));
			}
		}
	};

	// Update the email handler
	const handleEmailChange = (e, field, contactIndex) => {
		const value = e.target.value;
		const isValid = validateEmail(value);

		if (typeof contactIndex === "number") {
			// Handle additional contacts
			const newContacts = [...additionalContacts];
			newContacts[contactIndex].emailAddress = value;
			newContacts[contactIndex].emailValid = isValid;
			setAdditionalContacts(newContacts);
		} else {
			// Handle primary contacts as before
			if (field === "primary") {
				setDealerInfo((prev) => ({ ...prev, emailAddress: value }));
				setValidation((prev) => ({ ...prev, emailAddress: isValid }));
			} else if (field === "secondary") {
				setDealerInfo((prev) => ({ ...prev, emailAddress23: value }));
				setValidation((prev) => ({ ...prev, emailAddress23: isValid }));
			} else if (field === "tertiary") {
				setDealerInfo((prev) => ({ ...prev, emailAddress72: value }));
				setValidation((prev) => ({ ...prev, emailAddress72: isValid }));
			}
		}
	};

	const [notFoundMessage, setNotFoundMessage] = useState(false);

	// Read agency query parameter and pre-select agency on mount
	useEffect(() => {
		const agencyParam = searchParams.get("agency");
		if (agencyParam) {
			// Map query parameter to agency value (case-insensitive)
			const agencyValue = agencyParam.toLowerCase();
			let selectedAgency = "";

			if (agencyValue === "constellation") {
				selectedAgency = "Constellation";
			} else if (agencyValue === "purecars") {
				selectedAgency = "PureCars";
			}

			if (selectedAgency) {
				setSelections((prev) => ({
					...prev,
					agency: selectedAgency,
				}));
			}
		}
	}, [searchParams]);

	const handleDealerCodeChange = (e) => {
		let inputCode = e.target.value.trim();

		inputCode = inputCode.replace(/[^0-9-]/g, "");

		const matchingDealerCode = Object.keys(dealerData).find(
			(code) => code.replace(/-/g, "") === inputCode.replace(/-/g, ""),
		);

		const dealershipName = matchingDealerCode
			? dealerData[matchingDealerCode]
			: "";

		setDealerInfo((prev) => ({
			...prev,
			dealerCode: inputCode,
			dealershipName: dealershipName,
		}));

		setNotFoundMessage(!dealershipName);
	};

	// Handler for navigating to the next page
	const handleNext = (e) => {
		e.preventDefault();

		// Check if both selections are made
		if (!selections.agency || !selections.package) {
			setPackageError(true);
			window.scrollTo({ top: 0, behavior: "auto" });
			return;
		}

		setPackageError(false);
		setCurrentPage(2);
		window.scrollTo({ top: 0, behavior: "auto" });
	};

	const handleSelectionChange = (e) => {
		const { name, value } = e.target;

		// Remove the prefixes and clean up the name
		const cleanedName = name.replace(
			/^(q3_|q4_|q14_|q17_|q18_|q19_|q20_|q22_|q23_|q24_)/,
			"",
		);

		setSelections((prev) => {
			const updatedSelections = {
				...prev,
				[cleanedName]: value,
			};

			// Check if both selections are made
			const bothSelected =
				updatedSelections.agency && updatedSelections.package;

			// Set the package error state based on the selection
			setPackageError(!bothSelected);

			return updatedSelections;
		});
	};

	// Add handleBack function near other handlers
	const handleBack = () => {
		setCurrentPage(1);
		window.scrollTo({ top: 0, behavior: "auto" });
	};

	// Function to add contact section
	const handleAddContact = () => {
		if (additionalContacts.length < 5) {
			setAdditionalContacts([
				...additionalContacts,
				{
					dealershipContact: { first: "", last: "" },
					emailAddress: "",
					phoneNumber: { full: "" },
					emailValid: true,
					phoneValid: true,
				},
			]);
			setOpenContactSections([
				...openContactSections,
				additionalContacts.length,
			]);
		}
	};

	// Function to remove contact section
	const handleRemoveContact = (index) => {
		const newContacts = additionalContacts.filter((_, i) => i !== index);
		setAdditionalContacts(newContacts);
		// Remove the index from openContactSections and reindex remaining sections
		const newOpenSections = openContactSections
			.filter((i) => i !== index)
			.map((i) => (i > index ? i - 1 : i));
		setOpenContactSections(newOpenSections);
	};

	// Update handleSubmit to merge additionalContacts with dealerInfo before storing
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Check if all validations are true
		const allValid =
			Object.values(validation).every((valid) => valid) &&
			additionalContacts.every(
				(contact) => contact.emailValid && contact.phoneValid,
			);

		if (!allValid) {
			alert("Please fill out all required fields correctly.");
			return;
		}

		try {
			// Map additional contacts to dealerInfo structure before storing
			const updatedDealerInfo = { ...dealerInfo };

			additionalContacts.forEach((contact, index) => {
				const fieldMappings = [
					{
						contact: "dealershipContact22",
						email: "emailAddress23",
						phone: "phoneNumber24",
					},
					{
						contact: "dealershipContact56",
						email: "emailAddress57",
						phone: "phoneNumber58",
					},
					{
						contact: "dealershipContact61",
						email: "emailAddress62",
						phone: "phoneNumber63",
					},
					{
						contact: "dealershipContact66",
						email: "emailAddress67",
						phone: "phoneNumber68",
					},
					{
						contact: "dealershipContact71",
						email: "emailAddress72",
						phone: "phoneNumber73",
					},
				];

				if (index < fieldMappings.length) {
					const mapping = fieldMappings[index];
					updatedDealerInfo[mapping.contact] =
						contact.dealershipContact;
					updatedDealerInfo[mapping.email] = contact.emailAddress;
					updatedDealerInfo[mapping.phone] = contact.phoneNumber;
				}
			});

			// Store the merged data
			localStorage.setItem(
				"dealerInfo",
				JSON.stringify(updatedDealerInfo),
			);

			// Create form data object with existing fields
			const formData = {
				q3_agency: selections.agency,
				q4_package: selections.package,
				q17_dealerCode: updatedDealerInfo.dealerCode,
				q18_dealershipName: updatedDealerInfo.dealershipName,
				"q14_dealershipContact[first]":
					updatedDealerInfo.dealershipContact.first,
				"q14_dealershipContact[last]":
					updatedDealerInfo.dealershipContact.last,
				q19_emailAddress: updatedDealerInfo.emailAddress,
				"q20_phoneNumber[full]": updatedDealerInfo.phoneNumber.full,

				"q56_dealershipContact56[first]":
					updatedDealerInfo.dealershipContact56.first,
				"q56_dealershipContact56[last]":
					updatedDealerInfo.dealershipContact56.last,
				q57_emailAddress57: updatedDealerInfo.emailAddress57,
				"q58_phoneNumber58[full]": updatedDealerInfo.phoneNumber58.full,

				"q61_dealershipContact61[first]":
					updatedDealerInfo.dealershipContact61.first,
				"q61_dealershipContact61[last]":
					updatedDealerInfo.dealershipContact61.last,
				q62_emailAddress62: updatedDealerInfo.emailAddress62,
				"q63_phoneNumber63[full]": updatedDealerInfo.phoneNumber63.full,

				"q66_dealershipContact66[first]":
					updatedDealerInfo.dealershipContact66.first,
				"q66_dealershipContact66[last]":
					updatedDealerInfo.dealershipContact66.last,
				q67_emailAddress67: updatedDealerInfo.emailAddress67,
				"q68_phoneNumber68[full]": updatedDealerInfo.phoneNumber68.full,

				"q71_dealershipContact71[first]":
					updatedDealerInfo.dealershipContact71.first,
				"q71_dealershipContact71[last]":
					updatedDealerInfo.dealershipContact71.last,
				q72_emailAddress72: updatedDealerInfo.emailAddress72,
				"q73_phoneNumber73[full]": updatedDealerInfo.phoneNumber73.full,

				"q22_dealershipContact22[first]":
					updatedDealerInfo.dealershipContact22.first,
				"q22_dealershipContact22[last]":
					updatedDealerInfo.dealershipContact22.last,
				q23_emailAddress23: updatedDealerInfo.emailAddress23,
				"q24_phoneNumber24[full]": updatedDealerInfo.phoneNumber24.full,
			};

			// Map additional contacts to specific JotForm fields
			additionalContacts.forEach((contact, index) => {
				const fieldMappings = [
					{
						contact: "q56_dealershipContact56",
						email: "q57_emailAddress57",
						phone: "q58_phoneNumber58",
					},
					{
						contact: "q61_dealershipContact61",
						email: "q62_emailAddress62",
						phone: "q63_phoneNumber63",
					},
					{
						contact: "q66_dealershipContact66",
						email: "q67_emailAddress67",
						phone: "q68_phoneNumber68",
					},
					{
						contact: "q71_dealershipContact71",
						email: "q72_emailAddress72",
						phone: "q73_phoneNumber73",
					},
					{
						contact: "q22_dealershipContact22",
						email: "q23_emailAddress23",
						phone: "q24_phoneNumber24",
					},
				];

				if (index < fieldMappings.length) {
					const mapping = fieldMappings[index];
					formData[`${mapping.contact}[first]`] =
						contact.dealershipContact.first;
					formData[`${mapping.contact}[last]`] =
						contact.dealershipContact.last;
					formData[mapping.email] = contact.emailAddress;
					formData[`${mapping.phone}[full]`] =
						contact.phoneNumber.full;
				}
			});

			// Store selections in localStorage
			localStorage.setItem(
				"enrollmentSelections",
				JSON.stringify(selections),
			);

			// Submit the form
			const form = document.getElementById(FORM_ID);
			form.action = `https://submit.jotform.com/submit/${FORM_ID}`;
			form.method = "post";

			// Add all formData to the form before submitting
			Object.entries(formData).forEach(([key, value]) => {
				if (!form.querySelector(`[name="${key}"]`)) {
					const input = document.createElement("input");
					input.type = "hidden";
					input.name = key;
					input.value = value;
					form.appendChild(input);
				}
			});

			// Add checkbox fields to formData if checked (based on selected agency)
			if (selections.agency === "Constellation") {
				const checkbox25 = document.getElementById("input_25_0");
				if (checkbox25 && checkbox25.checked) {
					const checkbox25Value = checkbox25.value;
					if (!form.querySelector(`[name="q25_input28[]"]`)) {
						const input = document.createElement("input");
						input.type = "hidden";
						input.name = "q25_input28[]";
						input.value = checkbox25Value;
						form.appendChild(input);
					}
				}
			} else if (selections.agency === "PureCars") {
				const checkbox76 = document.getElementById("input_76_0");
				if (checkbox76 && checkbox76.checked) {
					const checkbox76Value = checkbox76.value;
					if (!form.querySelector(`[name="q76_input28[]"]`)) {
						const input = document.createElement("input");
						input.type = "hidden";
						input.name = "q76_input28[]";
						input.value = checkbox76Value;
						form.appendChild(input);
					}
				}
			}

			form.submit();
		} catch (error) {
			console.error("Form submission failed:", error);
		}
	};
	const isSubmitDisabled = Object.values(validation).some((valid) => !valid);

	// Render additional contacts section
	const renderAdditionalContacts = () => {
		return (
			<div className="additional-contacts-section">
				{additionalContacts.map((contact, index) => (
					<div key={index}>
						<li
							className="form-input-wide input-collapse-question"
							style={{ clear: "both" }}>
							{}
						</li>

						{openContactSections.includes(index) && (
							<div className="additional-contact-collapse">
								<InputField
									isFullName
									label="Dealership Contact"
									fullNameValues={contact.dealershipContact}
									fullNameOnChange={(field, value) => {
										const newContacts = [
											...additionalContacts,
										];
										newContacts[index].dealershipContact[
											field
										] = value;
										setAdditionalContacts(newContacts);
									}}
								/>

								<InputField
									type="email"
									label="Email Address"
									value={contact.emailAddress}
									onChange={(e) =>
										handleEmailChange(e, null, index)
									}
									onBlur={() => {
										const isValidEmail = validateEmail(
											contact.emailAddress,
										);
										const newContacts = [
											...additionalContacts,
										];
										newContacts[index].emailValid =
											isValidEmail;
										setAdditionalContacts(newContacts);
									}}
									error={
										!contact.emailValid &&
										contact.emailAddress !== ""
									}
									errorMessage="Please enter a valid email address"
								/>
								{!contact.emailValid &&
									contact.emailAddress !== "" && (
										<p
											style={{
												color: "red",
												fontSize: "10px",
												marginTop: "0",
												marginLeft: "20px",
												padding: "0",
											}}>
											*Please enter a valid email address.
										</p>
									)}

								<InputField
									type="tel"
									label="Phone Number"
									value={contact.phoneNumber.full}
									onChange={(e) => {
										const value = e.target.value.replace(
											/\D/g,
											"",
										);
										handlePhoneChange(
											{ target: { value } },
											null,
											index,
										);
									}}
									onBlur={() => {
										const isValidPhone = validatePhone(
											contact.phoneNumber.full,
										);
										const newContacts = [
											...additionalContacts,
										];
										newContacts[index].phoneValid =
											isValidPhone;
										setAdditionalContacts(newContacts);
									}}
									placeholder="(000) 000-0000"
									error={
										!contact.phoneValid &&
										contact.phoneNumber.full !== ""
									}
									errorMessage="Please enter a valid phone number"
								/>
								{!contact.phoneValid &&
									contact.phoneNumber.full !== "" && (
										<p
											style={{
												color: "red",
												fontSize: "10px",
												marginTop: "0",
												marginLeft: "20px",
												padding: "0",
											}}>
											*Please enter a complete phone
											number.
										</p>
									)}

								<div className="remove-contact-button">
									<CollapseButton
										onClick={() =>
											handleRemoveContact(index)
										}
										variant="collapse"
									/>
								</div>
							</div>
						)}
					</div>
				))}

				{additionalContacts.length < 5 && (
					<CollapseButton
						onClick={handleAddContact}
						variant="expand"
					/>
				)}
			</div>
		);
	};

	return (
		<>
			<ProgressBar currentPage={currentPage} />
			<div className="enrollment-container">
				<div className="form-wrapper">
					<form
						onSubmit={handleSubmit}
						className="jotform-form"
						id={FORM_ID}
						acceptCharset="utf-8"
						autoComplete="on">
						<input type="hidden" name="formID" value={FORM_ID} />
						<input type="hidden" id="JWTContainer" value="" />
						<input
							type="hidden"
							id="cardinalOrderNumber"
							value=""
						/>

						<div role="main" className="form-all">
							{currentPage === 1 && (
								<ul
									className="form-section page-section"
									role="presentation">
									{/* Page 1: Form Header and Radio Buttons */}

									{/* SELECT YOUR AGENCY */}
									<RadioGroup
										id="id_3"
										label="SELECT YOUR AGENCY"
										name="q3_agency"
										selectedValue={selections.agency}
										onChange={handleSelectionChange}
										options={[
											{
												value: "Constellation",
												label: "Constellation",
											},
											{
												value: "PureCars",
												label: "PureCars",
											},
										]}
									/>

									{/* SELECT YOUR PACKAGE */}
									<RadioGroup
										id="id_4"
										label="SELECT YOUR PACKAGE"
										name="q4_package"
										selectedValue={selections.package}
										onChange={handleSelectionChange}
										options={[
											{
												value: "Premium: $4,000/month",
												label: "Premium: $4,000/month",
											},
											{
												value: "Executive: $7,500/month",
												label: "Executive: $7,500/month",
											},
										]}
									/>

									{/* Informational Text */}
									<li data-type="control_text" id="id_28">
										<div
											id="cid_28"
											className="disclaimer-questions"
											data-layout="full">
											<div
												id="text_28"
												className="form-html"
												data-component="text"
												tabIndex="-1">
												<p>
													Please reach out to your BMW
													Eastern Region Marketing
													Manager, Daniel Nentwich{" "}
													<a
														style={{
															color: "rgba(0, 0, 0, 0.5)",
														}}
														href="mailto:daniel.nentwich@bmwna.com">
														daniel.nentwich@bmwna.com
													</a>
													, with any questions.
												</p>
												<br />
											</div>
										</div>
									</li>

									{/* Divider */}
									<li
										data-type="control_divider"
										className="divider"
										id="id_11">
										<div
											id="cid_11"
											className="form-input-wide"
											data-layout="full">
											<div
												className="divider"
												data-component="divider"
												style={{
													borderBottomWidth: "1px",
													borderBottomStyle: "solid",
													borderColor: "#e6e6e6",
													height: "1px",
													marginLeft: "0px",
													marginRight: "0px",
													marginTop: "5px",
													marginBottom: "5px",
												}}></div>
										</div>
									</li>
								</ul>
							)}

							{currentPage === 2 && (
								<ul
									className="form-section page-section page-two"
									role="presentation">
									{/* Page 2: Additional Fields */}
									{/* Dealer Code */}
									<div className="Dealer-header">
										<h2>DEALER INFORMATION</h2>
									</div>
									{/* Dealer Code Input */}
									<InputField
										id="id_17"
										type="text"
										name="q17_dealerCode"
										label="Dealer Code"
										value={dealerInfo.dealerCode}
										onChange={(e) => {
											// Remove all non-digits
											let value = e.target.value.replace(
												/\D/g,
												"",
											);

											// Format the dealerCode as "12345-67"
											if (value.length > 5) {
												value =
													value.slice(0, 5) +
													"-" +
													value.slice(5, 7); // Add hyphen after the 5th digit
											}

											// Update the dealerCode in state
											setDealerInfo((prev) => ({
												...prev,
												dealerCode: value,
											}));

											// Call the handleDealerCodeChange
											handleDealerCodeChange(e);
										}}
										required
										placeholder="Please Include Location ID"
										dataType="input-textbox"
										dataComponent="textbox"
										pattern="\d{5}-\d{2}"
										inputMode="numeric"
									/>

									{notFoundMessage && (
										<p
											style={{
												color: "#808080",
												fontSize: "10px",
												marginTop: "0",
												marginLeft: "20px",
												padding: "0",
											}}>
											Dealer code not found.
										</p>
									)}

									{/* Dealership Name Input */}
									<InputField
										id="id_18"
										type="text"
										pattern="[a-zA-Z]*"
										name="q18_dealershipName"
										label="Dealership Name"
										value={dealerInfo.dealershipName}
										onChange={(e) =>
											setDealerInfo((prev) => ({
												...prev,
												dealershipName: e.target.value,
											}))
										}
										required
										dataType="input-textbox"
										dataComponent="textbox"
									/>

									{/* Dealership Contact */}
									<InputField
										id="id_14"
										isFullName
										name="q14_dealershipContact"
										label="Dealership Contact"
										fullNameValues={
											dealerInfo.dealershipContact
										}
										fullNameOnChange={(field, value) => {
											// Remove any non-letter characters
											const filteredValue = value.replace(
												/[^a-zA-Z\s]/g,
												"",
											);
											setDealerInfo((prev) => ({
												...prev,
												dealershipContact: {
													...prev.dealershipContact,
													[field]: filteredValue,
												},
											}));
										}}
										required
									/>

									{/* Email Address */}
									<InputField
										id="id_19"
										type="email"
										name="q19_emailAddress"
										label="Email Address"
										value={dealerInfo.emailAddress}
										onChange={(e) =>
											handleEmailChange(e, "primary")
										}
										onBlur={() => {
											// Check if the email address is valid
											const isValidEmail = validateEmail(
												dealerInfo.emailAddress,
											);
											setValidation((prev) => ({
												...prev,
												emailAddress: isValidEmail,
											}));

											// Show the error message if the email is invalid
											if (
												!isValidEmail &&
												dealerInfo.emailAddress !== ""
											) {
												setEmailError(true); // Show the error message
											} else {
												setEmailError(false); // Hide the error message if valid
											}
										}}
										required
										dataComponent="email"
										autoComplete="section-input_19 email"
										error={
											!validation.emailAddress &&
											dealerInfo.emailAddress !== ""
										}
										errorMessage="Please enter a valid email address"
									/>
									{emailError &&
										!validation.emailAddress &&
										dealerInfo.emailAddress !== "" && (
											<p
												style={{
													color: "red",
													fontSize: "10px",
													marginTop: "0",
													marginLeft: "20px",
													padding: "0",
												}}>
												*Please enter a valid email
												address.
											</p>
										)}

									{/* Phone Number */}
									<InputField
										id="id_20"
										type="tel"
										name="q20_phoneNumber[full]"
										label="Phone Number"
										value={dealerInfo.phoneNumber.full}
										onChange={(e) => {
											const value =
												e.target.value.replace(
													/\D/g,
													"",
												); // Strip non-digit characters
											handlePhoneChange(
												{ target: { value } },
												"primary",
											);
										}}
										onBlur={() => {
											// Check if the phone number is complete (10 digits after stripping non-digits)
											const isValidPhone =
												dealerInfo.phoneNumber.full.replace(
													/\D/g,
													"",
												).length === 10;
											setValidation((prev) => ({
												...prev,
												phoneNumber: isValidPhone,
											}));

											// Show the error message if the phone number is not valid
											if (!isValidPhone) {
												setPhoneError(true); // Show the error message
											}
										}}
										required
										placeholder="(000) 000-0000"
										dataType="mask-number"
										dataComponent="phone"
										autoComplete="section-input_20 tel-national"
									/>
									{phoneError &&
										dealerInfo.phoneNumber.full.replace(
											/\D/g,
											"",
										).length !== 10 && (
											<p
												style={{
													color: "red",
													fontSize: "10px",
													marginTop: "0",
													marginLeft: "20px",
													padding: "0",
												}}>
												*Please enter a complete phone
												number.
											</p>
										)}

									{/* Additional Contacts Section */}
									{renderAdditionalContacts()}

									{/* Terms and Conditions Checkbox - Constellation */}
									{selections.agency === "Constellation" && (
										<li
											className="form-line jf-required"
											data-type="control_checkbox"
											id="id_25">
											<label
												className="form-label form-label-top form-label-auto"
												htmlFor="input_25_0"
												id="label_25"
												aria-hidden="false"></label>
											<div
												id="cid_25"
												className="form-input-wide jf-required"
												data-layout="full">
												<div
													className="form-single-column"
													role="group"
													aria-labelledby="label_25"
													data-component="checkbox">
													<span
														className="form-checkbox-item"
														style={{
															clear: "left",
														}}>
														<span className="dragger-item"></span>
														<input
															aria-describedby="label_25"
															type="checkbox"
															className="form-checkbox validate[required]"
															id="input_25_0"
															name="q25_input28[]"
															value="Dealer agrees to pay the fees for the Services selected and in the amounts set forth in the online registration process or, if an order form was used, on the order form. The fees for the Services shall be billed to Dealer and dealer agrees to pay the media for the Services through Constellation directly. Constellation may invoice Dealer directly for the Services if authorized by BMW to do so, and Dealer agrees to pay such invoice within 30 days of receipt. If a cancellation occurs on or before the 10th of each calendar month, Dealer will be billed for the remainder of that month and Services shall cancel at the end of such month. If a cancellation occurs after the 10th of any month, Dealer will be billed for the remainder of that month and the following month. Any changes in advertising packages (e.g., to increase or reduce the amount of the package) will take effect in the month following the month in which the request for change is made so long as Dealer makes the request at least five (5) business days before the beginning of the next month.  Dealer also agrees to Constellation's Master Services Agreement. Terms not defined here shall have the meaning set forth in Constellation's Master Services Agreement. Dealer also agrees to be bound to Constellation's E-SIGN Act Policy available at the Master Services Agreement. The Master Services Agreement is available "
															required
														/>
														<label
															id="label_input_25_0"
															htmlFor="input_25_0">
															Dealer agrees to pay
															the fees for the
															Services selected
															and in the amounts
															set forth in the
															online registration
															process or, if an
															order form was used,
															on the order form.
															The fees for the
															Services shall be
															billed to Dealer and
															dealer agrees to pay
															the media for the
															Services through
															Constellation
															directly.
															Constellation may
															invoice Dealer
															directly for the
															Services if
															authorized by BMW to
															do so, and Dealer
															agrees to pay such
															invoice within 30
															days of receipt. If
															a cancellation
															occurs on or before
															the 10th of each
															calendar month,
															Dealer will be
															billed for the
															remainder of that
															month and Services
															shall cancel at the
															end of such month.
															If a cancellation
															occurs after the
															10th of any month,
															Dealer will be
															billed for the
															remainder of that
															month and the
															following month. Any
															changes in
															advertising packages
															(e.g., to increase
															or reduce the amount
															of the package) will
															take effect in the
															month following the
															month in which the
															request for change
															is made so long as
															Dealer makes the
															request at least
															five (5) business
															days before the
															beginning of the
															next month. Dealer
															also agrees to
															Constellation's
															Master Services
															Agreement. Terms not
															defined here shall
															have the meaning set
															forth in
															Constellation's
															Master Services
															Agreement. Dealer
															also agrees to be
															bound to
															Constellation's
															E-SIGN Act Policy
															available at the
															Master Services
															Agreement. The
															Master Services
															Agreement is
															available
															<span>
																{" "}
																<a
																	href={
																		TermsAgreement
																	}
																	target="_blank"
																	rel="noopener noreferrer"
																	style={{
																		color: "blue",
																		fontWeight:
																			"bold",
																	}}>
																	here
																</a>
															</span>
															<span className="form-required">
																*
															</span>
														</label>
													</span>
												</div>
											</div>
										</li>
									)}

									{/* Terms and Conditions Checkbox - PureCars */}
									{selections.agency === "PureCars" && (
										<li
											className="form-line jf-required"
											data-type="control_checkbox"
											id="id_76">
											<label
												className="form-label form-label-top form-label-auto"
												htmlFor="input_76_0"
												id="label_76"
												aria-hidden="false"></label>
											<div
												id="cid_76"
												className="form-input-wide jf-required"
												data-layout="full">
												<div
													className="form-single-column"
													role="group"
													aria-labelledby="label_76"
													data-component="checkbox">
													<span
														className="form-checkbox-item"
														style={{
															clear: "left",
														}}>
														<span className="dragger-item"></span>
														<input
															aria-describedby="label_76"
															type="checkbox"
															className="form-checkbox validate[required]"
															id="input_76_0"
															name="q76_input28[]"
															value="Dealer agrees to pay the fees for the Services selected and in the amounts set forth in the online registration process or, if an order form was used, on the order form. The fees for the Services shall be billed to Dealer and dealer agrees to pay the media for the Services through PureCars directly. PureCars may invoice Dealer directly for the Services if authorized by BMW to do so, and Dealer agrees to pay such invoice within 30 days of receipt. If a cancellation occurs on or before the 10th of each calendar month, Dealer will be billed for the remainder of that month and Services shall cancel at the end of such month. If a cancellation occurs after the 10th of any month, Dealer will be billed for the remainder of that month and the following month. Any changes in advertising packages (e.g., to increase or reduce the amount of the package) will take effect in the month following the month in which the request for change is made so long as Dealer makes the request at least five (5) business days before the beginning of the next month."
															required
														/>
														<label
															id="label_input_76_0"
															htmlFor="input_76_0">
															Dealer agrees to pay
															the fees for the
															Services selected
															and in the amounts
															set forth in the
															online registration
															process or, if an
															order form was used,
															on the order form.
															The fees for the
															Services shall be
															billed to Dealer and
															dealer agrees to pay
															the media for the
															Services through
															PureCars directly.
															PureCars may invoice
															Dealer directly for
															the Services if
															authorized by BMW to
															do so, and Dealer
															agrees to pay such
															invoice within 30
															days of receipt. If
															a cancellation
															occurs on or before
															the 10th of each
															calendar month,
															Dealer will be
															billed for the
															remainder of that
															month and Services
															shall cancel at the
															end of such month.
															If a cancellation
															occurs after the
															10th of any month,
															Dealer will be
															billed for the
															remainder of that
															month and the
															following month. Any
															changes in
															advertising packages
															(e.g., to increase
															or reduce the amount
															of the package) will
															take effect in the
															month following the
															month in which the
															request for change
															is made so long as
															Dealer makes the
															request at least
															five (5) business
															days before the
															beginning of the
															next month.
															<span className="form-required">
																*
															</span>
														</label>
													</span>
												</div>
											</div>
										</li>
									)}

									{/* Hidden Fields */}
									<li style={{ display: "none" }}>
										Should be Empty:{" "}
										<input
											type="text"
											name="website"
											value=""
										/>
									</li>
								</ul>
							)}
						</div>

						{/* Additional Scripts */}
						<script>{`window.enableEventObserver=true`}</script>
						<script>{`JotForm.showJotFormPowered = "new_footer";`}</script>
						<script>{`JotForm.poweredByText = "Powered by Jotform";`}</script>
						<input
							type="hidden"
							className="simple_spc"
							id="simple_spc"
							name="simple_spc"
							value="260067105259151"
						/>
						<script type="text/javascript">
							{`
                var all_spc = document.querySelectorAll("form[id='260067105259151'] .simple_spc");
                for (var i = 0; i < all_spc.length; i++) {
                  all_spc[i].value = "260067105259151-260067105259151";
                }
              `}
						</script>
					</form>
				</div>
				<div className="summary-wrapper">
					<SelectionSummary
						selections={selections}
						handleSelectionChange={handleSelectionChange}
					/>

					<div className="form-footer-text">
						{packageError && (
							<div className="error-message">
								*Please select both your agency and package.
							</div>
						)}
						<p className="form-footer-text-content">
							*All costs are media. No added fees.
						</p>
					</div>
					<div className="form-submit-wrapper">
						{currentPage === 1 && (
							<button
								type="button"
								styleName="blue"
								size="large"
								onClick={handleNext}>
								Next
							</button>
						)}
						{currentPage === 2 && (
							<div className="form-submit-wrapper">
								<button
									type="button"
									styleName="outline"
									size="large"
									onClick={handleBack}
									style={{
										marginBottom: "10px",
										display: "none",
									}}>
									Back
								</button>
								<button
									type="submit"
									form="260067105259151"
									styleName="blue"
									size="large"
									disabled={isSubmitDisabled}>
									Submit Enrollment
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default JotFormComponent;
